import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/services/api'
import { Card, Table, Pagination, Badge, Input, Select, Modal, Button } from '@/components/ui'
import { DEFAULT_PAGE_SIZE } from '@spoofio/shared'
import { formatDate } from '@/utils/format-date'
import type { AdminUserResponse, AdminOrganizationResponse } from '@spoofio/shared/types'

const DEBOUNCE_MS = 300

export function AdminUsersPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const searchParam = searchParams.get('search') ?? ''
  const orgParam = searchParams.get('orgId') ?? ''
  const pageParam = parseInt(searchParams.get('page') ?? '1', 10)

  const [searchInput, setSearchInput] = useState(searchParam)
  const [debouncedSearch, setDebouncedSearch] = useState(searchParam)
  const [orgFilter, setOrgFilter] = useState(orgParam)
  const [currentPage, setCurrentPage] = useState(pageParam)

  const [editUser, setEditUser] = useState<AdminUserResponse | null>(null)
  const [editFirstName, setEditFirstName] = useState('')
  const [editLastName, setEditLastName] = useState('')
  const [editPhone, setEditPhone] = useState('')
  const [resetUser, setResetUser] = useState<AdminUserResponse | null>(null)

  // Create user state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createEmail, setCreateEmail] = useState('')
  const [createFirstName, setCreateFirstName] = useState('')
  const [createLastName, setCreateLastName] = useState('')

  // Delete user state
  const [deleteUser, setDeleteUser] = useState<AdminUserResponse | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setCurrentPage(1)
    }, DEBOUNCE_MS)
    return () => { clearTimeout(timer) }
  }, [searchInput])

  const syncParams = useCallback(
    (search: string, orgId: string, page: number) => {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (orgId) params.set('orgId', orgId)
      if (page > 1) params.set('page', String(page))
      setSearchParams(params)
    },
    [setSearchParams]
  )

  useEffect(() => {
    syncParams(debouncedSearch, orgFilter, currentPage)
  }, [debouncedSearch, orgFilter, currentPage, syncParams])

  const offset = (currentPage - 1) * DEFAULT_PAGE_SIZE

  const { data: orgsData } = useQuery({
    queryKey: ['admin-organizations-dropdown'],
    queryFn: () => api.adminListOrganizations({ limit: 200 }),
    staleTime: 60_000,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users', debouncedSearch, orgFilter, offset],
    queryFn: () =>
      api.adminListUsers({
        search: debouncedSearch || undefined,
        orgId: orgFilter || undefined,
        limit: DEFAULT_PAGE_SIZE,
        offset,
      }),
    staleTime: 30_000,
  })

  const updateUser = useMutation({
    mutationFn: () => {
      if (!editUser) return Promise.reject(new Error('No user selected'))
      return api.adminUpdateUser(editUser.id, {
        firstName: editFirstName || undefined,
        lastName: editLastName || undefined,
        phone: editPhone || undefined,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setEditUser(null)
    },
  })

  const passwordReset = useMutation({
    mutationFn: () => {
      if (!resetUser) return Promise.reject(new Error('No user selected'))
      return api.adminPasswordReset(resetUser.id)
    },
    onSuccess: () => { setResetUser(null) },
  })

  const createUser = useMutation({
    mutationFn: () =>
      api.adminCreateUser({
        email: createEmail,
        firstName: createFirstName || undefined,
        lastName: createLastName || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setShowCreateModal(false)
      setCreateEmail('')
      setCreateFirstName('')
      setCreateLastName('')
    },
  })

  const deleteUserMutation = useMutation({
    mutationFn: () => {
      if (!deleteUser) return Promise.reject(new Error('No user selected'))
      return api.adminDeleteUser(deleteUser.id)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setDeleteUser(null)
    },
  })

  const handleOrgChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOrgFilter(e.target.value)
    setCurrentPage(1)
  }

  const openEditModal = (user: AdminUserResponse) => {
    setEditUser(user)
    setEditFirstName(user.firstName ?? '')
    setEditLastName(user.lastName ?? '')
    setEditPhone('')
  }

  const orgOptions = [
    { value: '', label: 'All Organizations' },
    ...(orgsData?.data.map((o: AdminOrganizationResponse) => ({
      value: o.id,
      label: o.name,
    })) ?? []),
  ]

  const columns = [
    {
      key: 'email',
      header: 'Email',
      render: (u: AdminUserResponse) => (
        <span className="font-medium text-gray-900">{u.email}</span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (u: AdminUserResponse) =>
        [u.firstName, u.lastName].filter(Boolean).join(' ') || '-',
    },
    {
      key: 'organizations',
      header: 'Organization(s)',
      render: (u: AdminUserResponse) =>
        u.organizations.length > 0 ? (
          <div className="space-y-1">
            {u.organizations.map((o) => (
              <div key={o.id} className="flex items-center gap-1">
                <span className="text-sm">{o.name}</span>
                <Badge variant={o.role === 'owner' ? 'primary' : 'gray'}>
                  {o.role}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-gray-400">None</span>
        ),
    },
    {
      key: 'mfa',
      header: 'MFA',
      render: (u: AdminUserResponse) => (
        <Badge variant={u.mfaEnabled ? 'success' : 'gray'}>
          {u.mfaEnabled ? 'Enabled' : 'Off'}
        </Badge>
      ),
    },
    {
      key: 'lastLoginAt',
      header: 'Last Login',
      render: (u: AdminUserResponse) => formatDate(u.lastLoginAt),
    },
    {
      key: 'actions',
      header: '',
      render: (u: AdminUserResponse) => (
        <div className="flex gap-2" onClick={(e) => { e.stopPropagation() }}>
          <Button size="sm" variant="secondary" onClick={() => { openEditModal(u) }}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setResetUser(u) }}>
            Reset Password
          </Button>
          <Button size="sm" variant="danger" onClick={() => { setDeleteUser(u) }}>
            Delete
          </Button>
        </div>
      ),
    },
  ]

  const totalPages = data ? Math.ceil(data.total / DEFAULT_PAGE_SIZE) : 1

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <Button onClick={() => { setShowCreateModal(true) }}>Create User</Button>
      </div>

      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <Input
            label="Search"
            placeholder="Search by email or name..."
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value) }}
            className="w-64"
          />
          <Select
            label="Organization"
            value={orgFilter}
            onChange={handleOrgChange}
            options={orgOptions}
            className="w-48"
          />
        </div>

        {error ? (
          <div className="text-center py-8">
            <p className="text-danger-600">Failed to load users</p>
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              data={data?.data ?? []}
              keyExtractor={(u) => u.id}
              isLoading={isLoading}
              emptyMessage="No users found"
            />
            {data && data.total > DEFAULT_PAGE_SIZE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={data.total}
                itemsPerPage={DEFAULT_PAGE_SIZE}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </Card>

      {/* Edit User Modal */}
      <Modal
        isOpen={editUser !== null}
        onClose={() => { setEditUser(null) }}
        title="Edit User"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setEditUser(null) }}>Cancel</Button>
            <Button
              isLoading={updateUser.isPending}
              onClick={() => { updateUser.mutate() }}
            >
              Save
            </Button>
          </div>
        }
      >
        {editUser && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">{editUser.email}</p>
            <Input
              label="First Name"
              name="firstName"
              value={editFirstName}
              onChange={(e) => { setEditFirstName(e.target.value) }}
            />
            <Input
              label="Last Name"
              name="lastName"
              value={editLastName}
              onChange={(e) => { setEditLastName(e.target.value) }}
            />
            <Input
              label="Phone"
              name="phone"
              value={editPhone}
              onChange={(e) => { setEditPhone(e.target.value) }}
              placeholder="Optional"
            />
          </div>
        )}
      </Modal>

      {/* Password Reset Confirmation */}
      <Modal
        isOpen={resetUser !== null}
        onClose={() => { setResetUser(null) }}
        title="Password Reset"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setResetUser(null) }}>Cancel</Button>
            <Button
              variant="danger"
              isLoading={passwordReset.isPending}
              onClick={() => { passwordReset.mutate() }}
            >
              Send Reset Email
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          This will send a password reset email to <strong>{resetUser?.email}</strong>.
          Are you sure you want to proceed?
        </p>
      </Modal>

      {/* Create User Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false) }}
        title="Create User"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setShowCreateModal(false) }}>Cancel</Button>
            <Button
              isLoading={createUser.isPending}
              disabled={!createEmail}
              onClick={() => { createUser.mutate() }}
            >
              Create
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {createUser.isError && (
            <p className="text-sm text-danger-600">
              {createUser.error.message || 'Failed to create user'}
            </p>
          )}
          <Input
            label="Email"
            name="email"
            type="email"
            value={createEmail}
            onChange={(e) => { setCreateEmail(e.target.value) }}
            required
          />
          <Input
            label="First Name"
            name="firstName"
            value={createFirstName}
            onChange={(e) => { setCreateFirstName(e.target.value) }}
            placeholder="Optional"
          />
          <Input
            label="Last Name"
            name="lastName"
            value={createLastName}
            onChange={(e) => { setCreateLastName(e.target.value) }}
            placeholder="Optional"
          />
        </div>
      </Modal>

      {/* Delete User Confirmation */}
      <Modal
        isOpen={deleteUser !== null}
        onClose={() => { setDeleteUser(null) }}
        title="Delete User"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setDeleteUser(null) }}>Cancel</Button>
            <Button
              variant="danger"
              isLoading={deleteUserMutation.isPending}
              onClick={() => { deleteUserMutation.mutate() }}
            >
              Delete User
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {deleteUserMutation.isError && (
            <p className="text-sm text-danger-600">
              {deleteUserMutation.error.message || 'Failed to delete user'}
            </p>
          )}
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <strong>{deleteUser?.email}</strong>?
            This will remove them from all organizations and cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  )
}
